import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ClickOutside from '../../../../components/ClickOutside';
import HelperService from '../../../../services/HelperService';
import './SankeyDiagram.scss';

const diagramCenterMarginTopAndBottom = 90;
const paddingBetweenFibers = 7;
const minFiberWidth = 0.5;

const SankeyDiagram = ({
	diagramArr,
	prepareColorsPositions = () => {},
	isMirrored = false,
	height = 240,
	width = 260,
	closeTooltipTrigger,
}) => {
	const [diagramList, setDiagramList] = useState([]);
	const [isInitDiagram, setIsInitDiagram] = useState(false);
	const [tooltip, setTooltip] = useState(null);

	const leftX = 0;
	const rightX = width;

	const middleX = (leftX + rightX) / 3;
	const controlX1 = leftX + middleX;
	const controlX2 = rightX - middleX;

	const prepareDiagramList = () => {
		const colorsPositions = [];
		const arr = diagramArr;
		let allPersents = 0;

		for (let i = 0; i < arr.length; i++) {
			const item = arr[i];
			const risk = item?.percent || 0;

			item.valCorrect = Math.max(risk, minFiberWidth);
			allPersents += item.valCorrect;
		}

		const paddingAll = paddingBetweenFibers * (arr.length + 1);
		const onePercentHeight = (height - paddingAll) / allPersents;
		const onePercentHeightRight = (height - diagramCenterMarginTopAndBottom * 2 - (arr.length + 1)) / allPersents;

		const coeffY = 100;

		for (let i = 0; i < arr.length; i++) {
			const item = arr[i];
			const prev = arr[i - 1];

			item.leftTopY = (prev?.leftBottomY || 0) + paddingBetweenFibers;
			item.leftBottomY = item?.leftTopY + item.valCorrect * onePercentHeight;
			item.rightTopY = (prev?.rightBottomY || diagramCenterMarginTopAndBottom) + 1;
			item.rightBottomY = item.rightTopY + item.valCorrect * onePercentHeightRight;

			item.middleTopY = (item.rightTopY - item.leftTopY) / coeffY;
			item.middleBottomY = (item.rightBottomY - item.leftBottomY) / coeffY;

			item.controlTopY1 = item.leftTopY - item.middleTopY;
			item.controlTopY2 = item.rightTopY + item.middleTopY;

			item.controlBottomY1 = item.leftBottomY - item.middleBottomY;
			item.controlBottomY2 = item.rightBottomY + item.middleBottomY;

			let lastColor = colorsPositions.at(-1);

			if (!lastColor || item.color !== lastColor.color) {
				colorsPositions.push({
					color: item.color,
					topPosition: item.leftTopY,
					bottomPosition: item.leftBottomY,
					percentsSum: item.percent,
				});
			} else {
				lastColor.bottomPosition = item.leftBottomY;
				lastColor.percentsSum += item.percent;
			}
		}

		prepareColorsPositions(colorsPositions);
		setDiagramList(arr);
	};

	useEffect(() => {
		if (diagramArr.length && width > 0 && height > 0 && !isInitDiagram) {
			setIsInitDiagram(true);
			prepareDiagramList();
		}
	}, [diagramArr, width, height]);

	const handleMouseEnter = (e, title, risk, color) => {
		setTooltip({
			x: 0,
			y: e,
			title,
			risk,
			color,
		});
	};

	const handleMouseLeave = () => setTooltip(null);

	useEffect(() => {
		setTooltip(null);
	}, [closeTooltipTrigger]);

	const portalTooltip = document.getElementById('AMLInsightBlock__for-tooltip');

	return (
		<ClickOutside
			onClickOutside={() => {
				handleMouseLeave();
			}}
		>
			<div
				className={classNames('SankeyDiagram', { mirrored: isMirrored })}
				onMouseLeave={handleMouseLeave}
				// onPointerLeave={handleMouseLeave}
			>
				<svg width={width} height={height} colorInterpolation='sRGB'>
					{diagramList?.map((el, key) => {
						return (
							<path
								key={key}
								className={el.color}
								d={`M${leftX} ${el.leftTopY} C ${controlX1} ${el.controlTopY1}, ${controlX2} ${el.controlTopY2}, ${rightX} ${el.rightTopY} 
                    L ${rightX} ${el.rightBottomY} C ${controlX2} ${el.controlBottomY2}, ${controlX1} ${el.controlBottomY1}, ${leftX} ${el.leftBottomY} Z`}
								onPointerEnter={() => handleMouseEnter(el.leftTopY, el?.title, el?.percent, el?.color)}
								onPointerMove={e => {
									e.currentTarget.setPointerCapture(e.pointerId);
									handleMouseEnter(el.leftTopY, el?.title, el?.percent, el?.color);
								}}
								onPointerDown={e => {
									e.currentTarget.setPointerCapture(e.pointerId);
									handleMouseEnter(el.leftTopY, el?.title, el?.percent, el?.color);
								}}
							/>
						);
					})}
				</svg>

				{portalTooltip &&
					createPortal(
						<AnimatePresence>
							{!!tooltip && (
								<motion.div
									className={classNames('SankeyDiagram__tooltip', { mirrored: isMirrored })}
									style={{
										top: tooltip.y - 3,
									}}
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={{ duration: 0.2 }}
								>
									<div className='SankeyDiagram__tooltip-arrow' />
									{tooltip.title}
									<span className={tooltip?.color}>
										{tooltip.risk > 0 ? HelperService.FormatPercent(tooltip.risk, 2).replace(',', '.') : '< 0.01'}%
									</span>
								</motion.div>
							)}
						</AnimatePresence>,
						portalTooltip
					)}
			</div>
		</ClickOutside>
	);
};

export default SankeyDiagram;
